import { useState, useEffect, useRef, useCallback } from 'react';

const BASE_DELAY = 1000;
const MAX_DELAY = 30000;   // keep retrying forever, at most every 30 s
// (was: give up after 10 attempts ≈ 17 min → the live dashboard stayed frozen
// after any longer backend outage until a manual page reload)

export default function useWebSocket(url) {
  const [data, setData] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const wsRef = useRef(null);
  const retriesRef = useRef(0);
  const timeoutRef = useRef(null);
  const mountedRef = useRef(true);

  const connect = useCallback(() => {
    if (!url || !mountedRef.current) return;
    try {
      // Append the JWT so the backend can authenticate the socket (WebSocket
      // can't send Authorization headers). Backend reads ?token=.
      const tok = localStorage.getItem('npz_token');
      const authedUrl = tok
        ? `${url}${url.includes('?') ? '&' : '?'}token=${encodeURIComponent(tok)}`
        : url;
      const ws = new WebSocket(authedUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        if (!mountedRef.current) return;
        setIsConnected(true);
        retriesRef.current = 0;
      };

      ws.onmessage = (event) => {
        if (!mountedRef.current) return;
        try {
          setData(JSON.parse(event.data));
        } catch {
          setData(event.data);
        }
      };

      ws.onclose = (event) => {
        if (!mountedRef.current) return;
        setIsConnected(false);
        // 1008 = the server rejected our JWT (expired/invalid). Retrying with the
        // same token can never succeed — send the user to log in again.
        if (event?.code === 1008) {
          localStorage.removeItem('npz_token');
          window.location.href = '/login';
          return;
        }
        const delay = Math.min(BASE_DELAY * Math.pow(2, retriesRef.current), MAX_DELAY);
        retriesRef.current += 1;
        timeoutRef.current = setTimeout(connect, delay);
      };

      ws.onerror = () => ws.close();
    } catch (e) {
      console.error('WebSocket error:', e);
    }
  }, [url]);

  useEffect(() => {
    mountedRef.current = true;
    connect();
    return () => {
      mountedRef.current = false;
      clearTimeout(timeoutRef.current);
      wsRef.current?.close();
    };
  }, [connect]);

  const send = useCallback((message) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    }
  }, []);

  return { data, isConnected, send };
}
